// src/features/variant_workflow/frequency_data/MaximumCredibleModal.tsx
import { Alert, Button, Divider, Input, Select, Slider, Tooltip } from 'antd';
import { Formik } from 'formik';
import React from 'react';
import { useSelector } from 'react-redux';

import FullPageLoader from '../../../foundation/components/full_page_loader/FullPageLoader';
import Modal from '../../../foundation/components/modal/Modal';
import { ACCENT_BLUE } from '../../../foundation/config/colors';
import { formatPercentValue } from '../../../foundation/utils/percentageHandler';
import { useVariantAccess } from '../hooks/useVariantAccess';
import { selectVariantLock } from '../redux';
import { MaxAFScoring } from '../redux';
import { maxCredibleValidationSchema } from '../validation_schemas/max_credible_validation_schema';
import { useMaximumCredibleModalLogic } from './hooks';

type SampleModalType = {
  closeModal: () => void;
  record?: MaxAFScoring;
};

const HeterogeneitySlider = ({
  label,
  value,
  onChange,
  error,
  explanation = '',
  min = 0,
  max = 1,
  step = 0.00001,
  tooltipTitle,
  marks,
  valueFormatter,
  disabled,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  error?: string;
  explanation?: string;
  min?: number;
  max?: number;
  step?: number;
  tooltipTitle: any;
  marks?: Record<number, string>;
  valueFormatter?: (value: number) => string;
  disabled?: boolean;
}) => {
  const pct = max === min ? 0 : ((value - min) / (max - min)) * 100;
  // Round to the same precision as `step` so initial DB values don't show as raw floats
  const stepDecimals = step > 0 && step < 1 ? Math.round(-Math.log10(step)) : 0;
  const displayValue = valueFormatter
    ? valueFormatter(value)
    : parseFloat(value.toFixed(stepDecimals)).toString();
  // Anchor the label so it never overflows the slider edges
  const labelTranslate = pct < 5 ? '0%' : pct > 95 ? '-100%' : '-50%';

  return (
    <div className="c-form-field">
      <Tooltip
        title={tooltipTitle}
        overlayClassName="molecular-impact__tooltip credible-modal__tooltip"
        placement="bottom"
      >
        <div className="c-form-field__label c-form-field__label--slider">
          {label}
        </div>
      </Tooltip>
      <div className="c-form-field__wrapper allelic-heterogeneity-slider-wrapper">
        <div className="slider-with-value-label">
          <div
            className="slider-value-label"
            style={{
              left: `${pct}%`,
              transform: `translateX(${labelTranslate})`,
            }}
          >
            {displayValue}
          </div>
          <Slider
            min={min}
            max={max}
            step={step}
            value={typeof value === 'number' ? value : 0}
            onChange={onChange}
            disabled={disabled}
            marks={
              marks || {
                [min]: min.toString(),
                [max]: max.toString(),
              }
            }
            tooltip={{ open: false }}
            styles={{
              track: { background: ACCENT_BLUE },
            }}
          />
        </div>
        <div className="allelic-heterogeneity-explanation">{explanation}</div>
        <div className="c-form-field__error">{error ? error : undefined}</div>
      </div>
    </div>
  );
};

const MaximumCredibleModal = ({ closeModal, record }: SampleModalType) => {
  const lock = useSelector(selectVariantLock);
  const { canInteract } = useVariantAccess();

  // All logic extracted to custom hook for separation of concerns
  const {
    isLoading,
    isReadOnly,
    initialValues,
    populationSize,
    modalOpts,
    getIsSaveDisabled,
    handleFormSubmit,
    calculateEstimatedAfFromValues,
    calculateMaxcAc,
    calculateMaxcAf,
  } = useMaximumCredibleModalLogic({ closeModal, record });
  return (
    <Modal {...modalOpts} className="h-proband-modal">
      {isLoading && <FullPageLoader />}

      {isReadOnly && lock && (
        <Alert
          message="Read-Only Mode"
          description={`This variant is currently locked by ${lock.locked_by} and cannot be edited.`}
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Formik
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        validationSchema={maxCredibleValidationSchema}
      >
        {({
          errors,
          handleChange,
          values,
          handleSubmit,
          handleBlur,
          isValid,
          dirty,
          setFieldValue,
        }) => {
          // Centralized disable logic via getIsSaveDisabled
          const isSaveDisabled = getIsSaveDisabled(isValid, dirty);

          return (
            <form onSubmit={handleSubmit}>
              <div className="c-form-field">
                <div className="c-form-field__label">Phenotype:</div>
                <div className="c-form-field__wrapper">
                  <Input
                    name="phenotype_id"
                    disabled
                    type="text"
                    value={record?.phenotype.preferred_term}
                  />
                </div>
              </div>

              <div className="c-form-field">
                <div className="c-form-field__label">Inheritance:</div>
                <div className="c-form-field__wrapper">
                  <Select
                    value={values.inheritance}
                    onChange={(v) => setFieldValue('inheritance', v)}
                    onBlur={handleBlur}
                    className="h-proband-modal__select-field"
                    options={[
                      {
                        label: 'Monoallelic',
                        value: 'monoallelic',
                      },
                      {
                        label: 'Biallelic',
                        value: 'biallelic',
                      },
                    ]}
                    allowClear
                    placeholder="Choose variant zygosity for sample"
                    disabled={!canInteract}
                  />
                  <div className="c-form-field__error">
                    {errors.inheritance ? errors.inheritance : undefined}
                  </div>
                </div>
              </div>

              <div className="c-form-field">
                <Tooltip
                  title={
                    <span>
                      Prevalence:
                      <br />
                      total number of people affected by selected phenotype in a
                      population. Expressed as e.g.
                      <br />
                      <div style={{ marginLeft: '1rem' }}>
                        1 / 1.000 people. Take highest value.
                        <br />1 / 10.000, if prevalence of a rare phenotype is
                        unknown.
                      </div>
                    </span>
                  }
                  overlayClassName="molecular-impact__tooltip"
                  placement="bottom"
                >
                  <div className="c-form-field__label">Prevalence:</div>
                </Tooltip>

                <div className="c-form-field__wrapper">
                  <Input
                    name="prevalence"
                    onChange={handleChange}
                    type="text"
                    value={values.prevalence}
                    onBlur={handleBlur}
                    placeholder="Enter prevalence"
                    readOnly={!canInteract}
                  />
                  <div className="c-form-field__error">
                    {errors.prevalence ? errors.prevalence : undefined}
                  </div>
                </div>
              </div>

              <HeterogeneitySlider
                label="Allelic heterogeneity"
                value={values.allelic_heterogeneity || 0}
                onChange={(v) => setFieldValue('allelic_heterogeneity', v)}
                error={errors.allelic_heterogeneity as string}
                tooltipTitle={
                  <span>
                    Alleleic heterogeneity:
                    <br />
                    the maximum proportion of variation within a gene that is
                    attributable to a single allele, e.g.
                    <br />
                    <div style={{ marginLeft: '1rem' }}>
                      {`0,1 if gene harboring 10 pathogenic variants of equal prevalence.`}
                      <br />
                      {`0,01 if > 100 variants in ClinVar are known to be pathogenic and`}
                      <br />
                      {`0,02 if >50 <100 variants in ClinVar are known to be pathogenic.`}
                    </div>
                    {`CAVE exceptions: e.g. CFTR p.Phe508del AF = 0,66 go to variant exception list.`}
                  </span>
                }
                disabled={!canInteract}
              />

              <HeterogeneitySlider
                label="Genetic heterogeneity"
                value={values.genetic_heterogeneity || 0}
                onChange={(v) => setFieldValue('genetic_heterogeneity', v)}
                error={errors.genetic_heterogeneity as string}
                tooltipTitle={
                  <span>
                    Genetic heterogeneity:
                    <br />
                    the maximum proportion of selected phenotype attributable to
                    variation in a single gene, e.g.
                    <br />
                    <div style={{ marginLeft: '1rem' }}>
                      {`0,1 if 10 genes cause the phenotype.`}
                      <br />
                      {`0,01 if > 100 genes are known to cause the phenotype and`}
                      <br />
                      {`0,02 if >50 and <100 genes are known to cause the phenotype.`}
                    </div>
                  </span>
                }
                disabled={!canInteract}
              />

              <HeterogeneitySlider
                label="Penetrance"
                value={values.penetrance || 0}
                onChange={(v) => setFieldValue('penetrance', v)}
                error={errors.penetrance as string}
                min={0}
                max={100}
                step={1}
                tooltipTitle={
                  <span>
                    Penetrance: <br />
                    {`estimate penetrance, set penetrance equivalent to the minimum that is 'clinically actionable' for the selected phenotype, e.g.`}
                    <br />
                    <div style={{ marginLeft: '1rem' }}>
                      {`<=25%  for low- e.g. CHEK2 for HBOC,`}
                      <br />
                      {`>25% <=75% for middle- e.g. BRCA1 for HBOC and`}
                      <br />
                      {`>75% for high-penetrant genes e.g. HTT for Huntington.`}
                    </div>
                  </span>
                }
                marks={{
                  0: 'low',
                  100: 'high',
                }}
                valueFormatter={(value) => `${value}%`}
                disabled={!canInteract}
              />

              <Divider />

              <div className="c-form-field">
                <div className="c-form-field__label">
                  Calculated allele frequency threshold
                </div>
                <div className="c-form-field__wrapper">
                  <div className="credible-modal__inline-field-wrapper">
                    <div className="credible-modal__inline-field-label">
                      maximum tolerated reference AC:
                    </div>
                    <div className="credible-modal__inline-field-value">
                      {calculateMaxcAc(values, populationSize)}
                    </div>
                  </div>
                  <div className="credible-modal__inline-field-wrapper">
                    <div className="credible-modal__inline-field-label">
                      maximum credible population AF [%]:
                    </div>
                    <div className="credible-modal__inline-field-value">
                      {formatPercentValue(calculateMaxcAf(values))}
                    </div>
                  </div>
                  <div className="credible-modal__inline-field-wrapper">
                    <div className="credible-modal__inline-field-label">
                      estimated population AF [%]:
                    </div>
                    <div className="credible-modal__inline-field-value">
                      {formatPercentValue(
                        calculateEstimatedAfFromValues(values),
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="c-form__btn-wrapper h-proband-modal__form-btn-wrapper">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="h-proband-modal__form-submit-btn"
                  disabled={isSaveDisabled}
                >
                  Save changes
                </Button>
              </div>
            </form>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default MaximumCredibleModal;
